import React, { useEffect, useRef } from 'react';

interface Blob {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  radius: number;
  baseHue: number;
  hue: number;
  saturation: number;
  lightness: number;
  alpha: number;
  speedX: number;
  speedY: number;
  phase: number;
  phaseSpeed: number;
}

const BLOB_COUNT = 7;

const FluidBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, active: false });
  const pointerRef = useRef({ x: 0.5, y: 0.5 });
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const onMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX / window.innerWidth;
      mouseRef.current.y = e.clientY / window.innerHeight;
      mouseRef.current.active = true;
    };
    const onLeave = () => {
      mouseRef.current.active = false;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseleave', onLeave);

    const blobs: Blob[] = [];
    for (let i = 0; i < BLOB_COUNT; i++) {
      const hue = (i / BLOB_COUNT) * 360;
      blobs.push({
        x: Math.random(),
        y: Math.random(),
        targetX: Math.random(),
        targetY: Math.random(),
        radius: 0.25 + Math.random() * 0.2,
        baseHue: hue,
        hue,
        saturation: 72 + Math.random() * 14,
        lightness: 48 + Math.random() * 8,
        alpha: 0.16 + Math.random() * 0.08,
        speedX: (Math.random() - 0.5) * 0.0003,
        speedY: (Math.random() - 0.5) * 0.0003,
        phase: Math.random() * Math.PI * 2,
        phaseSpeed: 0.003 + Math.random() * 0.004,
      });
    }

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      const mouse = mouseRef.current;
      const pointer = pointerRef.current;
      const now = performance.now() * 0.001;

      // Smooth the pointer for fluid motion rather than jittery tracking.
      pointer.x += (mouse.x - pointer.x) * 0.08;
      pointer.y += (mouse.y - pointer.y) * 0.08;

      ctx.fillStyle = '#08050d';
      ctx.fillRect(0, 0, w, h);

      for (const blob of blobs) {
        blob.phase += blob.phaseSpeed;
        blob.targetX += blob.speedX + Math.sin(blob.phase) * 0.0002;
        blob.targetY += blob.speedY + Math.cos(blob.phase * 0.7) * 0.0002;

        if (blob.targetX < -0.1 || blob.targetX > 1.1) blob.speedX *= -1;
        if (blob.targetY < -0.1 || blob.targetY > 1.1) blob.speedY *= -1;
        blob.targetX = Math.max(-0.2, Math.min(1.2, blob.targetX));
        blob.targetY = Math.max(-0.2, Math.min(1.2, blob.targetY));

        if (mouse.active) {
          const dx = pointer.x - blob.targetX;
          const dy = pointer.y - blob.targetY;
          const distance = Math.hypot(dx, dy);
          const attraction = Math.max(0, 1 - distance * 1.8);

          blob.targetX += dx * (0.004 + attraction * 0.016);
          blob.targetY += dy * (0.004 + attraction * 0.016);

          const pointerHue = ((pointer.x * 360 + now * 45) % 360 + 360) % 360;
          blob.hue = blob.baseHue + pointerHue * 0.5 + attraction * 80;
        } else {
          const driftHue = blob.baseHue + Math.sin(now * 0.4 + blob.phase) * 26;
          blob.hue += (driftHue - blob.hue) * 0.02;
        }

        blob.x += (blob.targetX - blob.x) * 0.02;
        blob.y += (blob.targetY - blob.y) * 0.02;

        const cx = blob.x * w;
        const cy = blob.y * h;
        const r = blob.radius * Math.max(w, h);

        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        const h1 = ((blob.hue % 360) + 360) % 360;
        grad.addColorStop(0, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness}%, ${blob.alpha})`);
        grad.addColorStop(0.5, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness * 0.7}%, ${blob.alpha * 0.4})`);
        grad.addColorStop(1, `hsla(${h1}, ${blob.saturation}%, ${blob.lightness * 0.5}%, 0)`);

        ctx.globalCompositeOperation = 'lighter';
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
      }

      if (mouse.active) {
        const px = pointer.x * w;
        const py = pointer.y * h;
        const pr = Math.max(w, h) * 0.42;
        const pointerHue = ((pointer.x * 360 + now * 45) % 360 + 360) % 360;

        const pointerGradient = ctx.createRadialGradient(px, py, 0, px, py, pr);
        pointerGradient.addColorStop(0, `hsla(${pointerHue}, 95%, 62%, 0.22)`);
        pointerGradient.addColorStop(0.38, `hsla(${(pointerHue + 120) % 360}, 88%, 56%, 0.12)`);
        pointerGradient.addColorStop(0.72, `hsla(${(pointerHue + 240) % 360}, 84%, 50%, 0.07)`);
        pointerGradient.addColorStop(1, 'hsla(0, 0%, 0%, 0)');

        ctx.globalCompositeOperation = 'screen';
        ctx.fillStyle = pointerGradient;
        ctx.fillRect(0, 0, w, h);
      }

      ctx.globalCompositeOperation = 'source-over';
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0"
      style={{ width: '100vw', height: '100vh', opacity: 0.9 }}
      aria-hidden="true"
    />
  );
};

export default FluidBackground;
